import React, { useState, useEffect } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Table,
	Spinner,
	Alert,
	Badge,
	Input,
	FormGroup,
	Label,
} from 'reactstrap';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';
import { toast } from 'react-toastify';

const AcceptApplicationsModal = ({ isOpen, toggle, onAccepted }) => {
	const [loading, setLoading] = useState(false);
	const [applications, setApplications] = useState([]);
	const [filteredApplications, setFilteredApplications] = useState([]);
	const [selectedApplications, setSelectedApplications] = useState([]);
	const [accepting, setAccepting] = useState(false);
	const [error, setError] = useState('');
	const [searchTerm, setSearchTerm] = useState('');

	useEffect(() => {
		if (isOpen) {
			fetchAnnexureReceivedApplications();
			setSearchTerm('');
			setSelectedApplications([]);
		}
	}, [isOpen]);

	// Filter applications based on search term
	useEffect(() => {
		if (!searchTerm.trim()) {
			setFilteredApplications(applications);
		} else {
			const searchLower = searchTerm.toLowerCase();
			const filtered = applications.filter(app => {
				const idMatch = app.application_id.toString().includes(searchLower);
				const nameMatch = app.applicant_name && app.applicant_name.toLowerCase().includes(searchLower);
				const emailMatch = app.applicant_email && app.applicant_email.toLowerCase().includes(searchLower);
				const programMatch = app.program && app.program.toLowerCase().includes(searchLower);
				
				return idMatch || nameMatch || emailMatch || programMatch;
			});
			setFilteredApplications(filtered);
		}
	}, [searchTerm, applications]);

	const fetchAnnexureReceivedApplications = async () => {
		try {
			setLoading(true);
			setError('');
			const token = sessionStorage.getItem('token');
			
			const response = await axios.get(
				`${BASE_URL}/api/admin/dashboard/annexure-received`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (response.data.success) {
				setApplications(response.data.data || []);
				setFilteredApplications(response.data.data || []);
			} else {
				setError('Failed to fetch applications');
			}
		} catch (err) {
			console.error('Error fetching annexure received applications:', err);
			setError(err.response?.data?.message || 'Failed to load applications');
		} finally {
			setLoading(false);
		}
	};

	const handleSelectAll = (e) => {
		if (e.target.checked) {
			setSelectedApplications(filteredApplications.map((app) => app.application_id));
		} else {
			setSelectedApplications([]);
		}
	};

	const handleSelectApplication = (applicationId) => {
		setSelectedApplications((prev) => {
			if (prev.includes(applicationId)) {
				return prev.filter((id) => id !== applicationId);
			} else {
				return [...prev, applicationId];
			}
		});
	};

	const handleAcceptApplications = async () => {
		if (selectedApplications.length === 0) {
			toast.warning('Please select at least one application');
			return;
		}

		try {
			setAccepting(true);
			setError('');
			const token = sessionStorage.getItem('token');

			const response = await axios.post(
				`${BASE_URL}/api/admin/dashboard/accept-applications`,
				{ applicationIds: selectedApplications },
				{
					headers: {
						Authorization: `Bearer ${token}`,
						'Content-Type': 'application/json',
					},
				}
			);

			if (response.data.success) {
				const { processed, ineligibleApplications } = response.data.data;
				
				if (processed > 0) {
					toast.success(`Successfully accepted ${processed} application(s)`);
				}
				
				if (ineligibleApplications && ineligibleApplications.length > 0) {
					toast.warning(`${ineligibleApplications.length} application(s) were ineligible and skipped`);
				}

				// Reset selections and refresh
				setSelectedApplications([]);
				fetchAnnexureReceivedApplications();
				
				// Notify parent component to refresh
				if (onAccepted) {
					onAccepted();
				}
			} else {
				setError(response.data.message || 'Failed to finalize candidates');
			}
		} catch (err) {
			console.error('Error accepting applications:', err);
			setError(err.response?.data?.message || 'Failed to finalize candidates');
			toast.error('Failed to finalize candidates');
		} finally {
			setAccepting(false);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return '-';
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	return (
		<Modal isOpen={isOpen} toggle={toggle} size="xl">
			<ModalHeader toggle={toggle}>
				Finalize Candidates with Annexure
			</ModalHeader>
			<ModalBody>
				{error && (
					<Alert color="danger" className="mb-3">
						{error}
					</Alert>
				)}

				{loading ? (
					<div className="text-center p-5">
						<Spinner color="primary" />
						<p className="mt-3">Loading applications...</p>
					</div>
				) : applications.length === 0 ? (
					<Alert color="info">
						No applications with submitted annexures are currently pending acceptance.
					</Alert>
				) : (
					<>
						<div className="mb-3">
							<p className="text-muted mb-2">
								<strong>{applications.length}</strong> application(s) with annexure submitted and ready for finalization.
								Select applications below and click "Finalize Candidates" to complete the process.
							</p>
							
							{/* Search Input */}
							<FormGroup className="mb-3">
								<Input
									type="text"
									placeholder="Search by Application ID, Name, Email, or Program..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									style={{ maxWidth: '500px' }}
								/>
								{searchTerm && (
									<small className="text-muted mt-1 d-block">
										Showing {filteredApplications.length} of {applications.length} application(s)
									</small>
								)}
							</FormGroup>

							<FormGroup check>
								<Label check>
									<Input
										type="checkbox"
										checked={selectedApplications.length === filteredApplications.length && filteredApplications.length > 0}
										onChange={handleSelectAll}
									/>
									{' '}Select All {searchTerm && `(${filteredApplications.length} filtered)`}
								</Label>
							</FormGroup>
						</div>

						<div style={{ maxHeight: '500px', overflowY: 'auto' }}>
							<Table hover bordered responsive>
								<thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
									<tr>
										<th style={{ width: '50px' }}>Select</th>
										<th>Application ID</th>
										<th>Applicant Name</th>
										<th>Email</th>
										<th>Program</th>
										<th>Annexure Submitted</th>
										<th>Annexure A</th>
										<th>Annexure B</th>
									</tr>
								</thead>
								<tbody>
									{filteredApplications.length === 0 ? (
										<tr>
											<td colSpan="8" className="text-center text-muted py-4">
												No applications found matching "{searchTerm}"
											</td>
										</tr>
									) : (
										filteredApplications.map((app) => (
											<tr key={app.application_id}>
											<td className="text-center">
												<Input
													type="checkbox"
													checked={selectedApplications.includes(app.application_id)}
													onChange={() => handleSelectApplication(app.application_id)}
												/>
											</td>
											<td>
												<Badge color="secondary">{app.application_id}</Badge>
											</td>
											<td>{app.applicant_name || '-'}</td>
											<td>{app.applicant_email || '-'}</td>
											<td>{app.program || '-'}</td>
											<td>{formatDate(app.annexure_submitted_at)}</td>
											<td>
												{app.annexure_a_url ? (
													<div className="d-flex gap-1">
														<a 
															href={app.annexure_a_url} 
															target="_blank" 
															rel="noopener noreferrer"
															className="btn btn-sm btn-info p-1"
															title="View Annexure A"
															style={{ fontSize: '0.85rem', lineHeight: '1' }}
														>
															<i className="bi bi-eye"></i>
														</a>
														<a 
															href={app.annexure_a_url} 
															download
															className="btn btn-sm btn-success p-1"
															title="Download Annexure A"
															style={{ fontSize: '0.85rem', lineHeight: '1' }}
														>
															<i className="bi bi-download"></i>
														</a>
													</div>
												) : (
													<Badge color="warning">Not Uploaded</Badge>
												)}
											</td>
											<td>
												{app.annexure_b_url ? (
													<div className="d-flex gap-1">
														<a 
															href={app.annexure_b_url} 
															target="_blank" 
															rel="noopener noreferrer"
															className="btn btn-sm btn-info p-1"
															title="View Annexure B"
															style={{ fontSize: '0.85rem', lineHeight: '1' }}
														>
															<i className="bi bi-eye"></i>
														</a>
														<a 
															href={app.annexure_b_url} 
															download
															className="btn btn-sm btn-success p-1"
															title="Download Annexure B"
															style={{ fontSize: '0.85rem', lineHeight: '1' }}
														>
															<i className="bi bi-download"></i>
														</a>
													</div>
												) : (
													<Badge color="warning">Not Uploaded</Badge>
												)}
											</td>
										</tr>
										))
									)}
								</tbody>
							</Table>
						</div>

						<Alert color="info" className="mt-3 mb-0">
							<strong>Note:</strong> Accepting these applications will send a final acceptance email to the applicants 
							and update their status to "Accepted". This action confirms their enrollment in the fellowship program.
						</Alert>
					</>
				)}
			</ModalBody>
			<ModalFooter>
				<div className="d-flex justify-content-between w-100 align-items-center">
					<div>
						{selectedApplications.length > 0 && (
							<span className="text-muted">
								{selectedApplications.length} application(s) selected
							</span>
						)}
					</div>
					<div>
						<Button color="secondary" onClick={toggle} disabled={accepting}>
							Cancel
						</Button>
						{' '}
						<Button
							color="success"
							onClick={handleAcceptApplications}
							disabled={accepting || selectedApplications.length === 0 || loading}
						>
							{accepting ? (
								<>
									<Spinner size="sm" className="me-2" />
									Finalizing...
								</>
							) : (
								'Finalize Candidates'
							)}
						</Button>
					</div>
				</div>
			</ModalFooter>
		</Modal>
	);
};

export default AcceptApplicationsModal;
